/* eslint-disable no-confusing-arrow */
import React from 'react';
import _has from 'lodash/has';
import { connect, ConnectedProps } from 'react-redux';
import { Action, ConfigService, withInject } from '@piwikpro/platform';
import {
  Stack,
  TFunction,
  HelpLink,
  withTranslation,
  formatDate,
} from '@piwikpro/ui-components';
import { Apps } from '@piwikpro/apps-crate';
import { Currency } from '@piwikpro/platform/config/currencies';
import currencies from '@piwikpro/platform/config/currencies.json';
import { Timezone } from '@piwikpro/platform/config/timeZones';
import { AdministrationState, STATE_ROOT } from '../../../../../redux';

const parseAnalyticsCurrencies = (currenciesList: string[][]) => currenciesList.map(currency => ({
  id: currency[0],
  value: currency[0],
  name: `${currency[1]} (${currency[0]})`,
}));


const mapStateToProps = (state: AdministrationState) => {
  const { settings } = state[STATE_ROOT].apps.details.settings;

  return ({
    app: settings as NonNullable<typeof settings>,
  });
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  apps: Apps
  config: ConfigService

}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

interface State {
  timezoneSearch: string
  currencySearch: string
}

class GeneralTimezoneAndCurrency extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      timezoneSearch: '',
      currencySearch: '',
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.clearSearchInput = this.clearSearchInput.bind(this);
    this.getAvailableTimezones = this.getAvailableTimezones.bind(this);
    this.getAvailableCurrencies = this.getAvailableCurrencies.bind(this);
  }

  getAvailableTimezones(searchValue, items) {
    const { app } = this.props;

    return items.filter(
      item => item.id !== app.attributes.timezone
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  getAvailableCurrencies(searchValue, items) {
    const { app } = this.props;

    return items.filter(
      item => item.id !== app.attributes.currency
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  searchValueMatchesItem(searchValue, item) {
    return _has(item, 'name')
      && item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
      && item.name.length >= searchValue.length;
  }

  handleSearchChange(e, field: keyof State) {
    this.setState({
      [field]: e.target.value,
    } as Pick<State, keyof State>);
  }

  clearSearchInput() {
    this.setState({
      timezoneSearch: '',
      currencySearch: '',
    });
  }

  async handleSubmit(values, { setErrors, setSubmitting, resetForm }) {
    const { apps, app } = this.props;

    try {
      if (values.currency) {
        await apps.update(app.id, values, { withConfirmation: true });
      } else {
        await apps.update(app.id, values);
      }
    } catch (err: any) {
      setErrors(err.errors);
      resetForm();
    }

    setSubmitting(false);
  }

  render() {
    const {
      app,
      config,
      t,
    } = this.props;
    const {
      timezoneSearch,
      currencySearch,
    } = this.state;
    const { timezone, currency } = app.attributes;
    const analyticsCurrencies = parseAnalyticsCurrencies(currencies);
    const hasOldCurrency = !analyticsCurrencies.find(newCurrency => newCurrency.id === currency);
    if (hasOldCurrency) {
      const currencyFromOldConfig = (config.get('AVAILABLE_CURRENCIES') as Currency).find(oldCurrency => oldCurrency.id === currency)!;
      analyticsCurrencies.push(currencyFromOldConfig);
    }
    const filteredTimezones = this.getAvailableTimezones(timezoneSearch, config.get('AVAILABLE_TIME_ZONES'));
    const filteredCurrencies = this.getAvailableCurrencies(
      currencySearch,
      analyticsCurrencies,
    );
    const timezoneOption = (config.get('AVAILABLE_TIME_ZONES') as Timezone)
      .find(option => option.id === timezone);

    return (
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item fill>
          <Action.AutoSavedFilteredSelectControl
            dataId="AppTimezoneControl"
            label={t('apps.details.settings.general.timezone.label')}
            info={timezoneOption ? t('apps.details.settings.general.timezone.help', {
              date: formatDate(new Date(), {
                format: 'LT',
                timeZone: timezoneOption.intlValue ?? timezoneOption.value,
              }),
              currentTimezone: timezone,
            }) : null}
            options={[{
              id: 'timezone-select-field',
              items: filteredTimezones,
            }]}
            name="timezone"
            search={timezoneSearch}
            onSearch={e => this.handleSearchChange(e, 'timezoneSearch')}
            onOpen={this.clearSearchInput}
            initialValue={timezone}
            onSubmit={this.handleSubmit}
            getSelectedName={value => filteredTimezones.find(option => value === option.id).name}
          />
        </Stack.Item>
        <Stack.Item fill>
          <Action.AutoSavedFilteredSelectControl
            dataId="AppCurrencyControl"
            label={t('apps.details.settings.general.currency.label')}
            info={t('apps.details.settings.general.currency-with-conversion.info')}
            options={[{
              id: 'currency-select-field',
              items: filteredCurrencies,
            }]}
            name="currency"
            search={currencySearch}
            onSearch={e => this.handleSearchChange(e, 'currencySearch')}
            onOpen={this.clearSearchInput}
            initialValue={currency}
            onSubmit={this.handleSubmit}
            touched={hasOldCurrency} // to trigger error displaying on initial render
            error={hasOldCurrency && t('apps.details.settings.general.currency-with-conversion.old-currency-selected.error')}
            getSelectedName={value => filteredCurrencies.find(option => value === option.id).name}
          />
        </Stack.Item>
        <Stack.Item>
          <HelpLink href="https://help.piwik.pro/support/getting-started/currency-conversion/">
            Currency conversion
          </HelpLink>
        </Stack.Item>
      </Stack>
    );
  }
}

export default connector(withInject<InjectedProps>({
  apps: 'AppsCrate.apps',
  config: 'config',
})(
  withTranslation(['administration'])(GeneralTimezoneAndCurrency),
));
